import * as React from 'react';
import { useInView } from 'react-intersection-observer';

import { IProduct } from '@containers/ProductListing/model';

import { dispatchProductsShownEvent } from '../hooks/useDispatchProductsShowEvent';
import { ISectionContainerProps, SectionContainer } from '../SectionContainer';

import { ProductListContent } from './ProductItemsContent';
import { useProductsByIdsBatched } from './useProductsByIdsBatched';

export interface IProductsSectionProps
  extends Omit<ISectionContainerProps, 'children'> {
  productIds: Array<IProduct['id']>;
  productIdsLoading?: boolean;
  trackProductClick?: boolean;
  trackProductImpression?: boolean;
  attributionToken?: string;
  itemsPerSlide?: number;
  titleClassName?: string;
  listContentClassName?: string;
  isSponsored?: boolean;
  onProductClick?: (productId: string) => void;
  onProductImpression?: (productId: string) => void;
}

export const ProductsSection: React.FC<IProductsSectionProps> = React.memo(
  ({
    productIds,
    listLabel = '',
    containerId = '',
    titleId = '',
    productIdsLoading = false,
    titleMessage = null,
    withDivider = false,
    trackProductClick = false,
    trackProductImpression = false,
    attributionToken,
    itemsPerSlide,
    titleClassName,
    listContentClassName,
    isSponsored = false,
    onProductClick,
    onProductImpression,
  }) => {
    const [ref, inView] = useInView({ triggerOnce: true });

    const { loading, data } = useProductsByIdsBatched(productIds, {
      skip: !inView || productIdsLoading,
      onCompleted: (response) => {
        dispatchProductsShownEvent(
          response?.vpProductByIds
            .filter((p): p is NonNullable<typeof p> => p !== null)
            .map((p) => ({ ...p, webId: p.id! })),
          {
            section: listLabel,
          }
        );
      },
    });

    const noProducts = !loading && !data?.vpProductByIds?.length;
    if (noProducts && inView && !productIdsLoading) {
      return null;
    }

    return (
      <SectionContainer
        containerId={containerId}
        key={containerId}
        withDivider={withDivider}
        titleMessage={titleMessage ?? undefined}
        titleId={titleId}
        ref={ref}
        titleClassName={titleClassName}
      >
        <ProductListContent
          className={listContentClassName}
          itemsPerSlide={itemsPerSlide}
          loading={loading || productIdsLoading}
          inView={inView}
          data={data}
          trackProductClick={trackProductClick}
          trackProductImpression={trackProductImpression}
          listLabel={listLabel}
          attributionToken={attributionToken}
          isSponsored={isSponsored}
          onProductClick={onProductClick}
          onProductImpression={onProductImpression}
        />
      </SectionContainer>
    );
  }
);
